import React, { useEffect, useRef } from 'react';
import { Text, Flex, Box } from "@radix-ui/themes";
import Icon from '@mdi/react';
import { mdiClose, mdiCurrencyUsd, mdiChartLine } from '@mdi/js';
import { useStore } from '../store/store';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the annotation plugin
Chart.register(annotationPlugin);

const SidebarAnalyticsCost = ({ onClose }) => {
  const [state] = useStore();
  const novel = state.currentNovel;
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  // Process cost data for the chart
  useEffect(() => {
    if (!novel || !chartRef.current) return;
    
    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    
    // Collect all scenes with their costs
    const allScenes = [];
    let totalCost = 0;
    let scenesWithCost = 0;
    
    novel.chapters?.forEach((chapter, chapterIndex) => {
      chapter.scenes?.forEach((scene, sceneIndex) => {
        // Use the scene's cost if available, otherwise default to 0
        const cost = scene.cost || 0;
        
        if (cost > 0) {
          totalCost += cost;
          scenesWithCost++;
        }
        
        allScenes.push({
          id: scene.id,
          chapterIndex,
          chapterTitle: chapter.title,
          sceneIndex,
          cost,
          label: `Ch ${chapterIndex + 1}, Scene ${sceneIndex + 1}`
        });
      });
    });
    
    // Filter to only scenes with costs and sort by chapter/scene order
    const scenesWithCostData = allScenes.filter(scene => scene.cost > 0);
    
    // If we don't have any cost data, don't create a chart
    if (scenesWithCostData.length === 0) {
      return;
    }
    
    // Calculate average cost per scene
    const averageCost = scenesWithCost > 0 ? totalCost / scenesWithCost : 0;
    
    // Prepare chart data
    const labels = scenesWithCostData.map(scene => scene.label);
    const data = scenesWithCostData.map(scene => scene.cost);
    
    // Create gradient for bars
    const ctx = chartRef.current.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(75, 154, 106, 0.8)');
    gradient.addColorStop(1, 'rgba(75, 154, 106, 0.2)');
    
    // Create the chart
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Cost ($)',
            data,
            backgroundColor: gradient,
            borderColor: 'rgba(75, 154, 106, 1)',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Cost (USD)',
              color: 'rgba(255, 255, 255, 0.8)'
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              callback: function(value) {
                return '$' + value.toFixed(4);
              }
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          },
          x: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        plugins: {
          legend: {
            labels: {
              color: 'rgba(255, 255, 255, 0.8)'
            }
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                const sceneIndex = context[0].dataIndex;
                const scene = scenesWithCostData[sceneIndex];
                return `${scene.chapterTitle} - Scene ${scene.sceneIndex + 1}`;
              },
              label: function(context) {
                return `Cost: $${context.raw.toFixed(6)}`;
              }
            }
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                yMin: averageCost,
                yMax: averageCost,
                borderColor: 'rgba(255, 255, 255, 0.7)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                  content: `Average: $${averageCost.toFixed(6)}`,
                  enabled: true,
                  position: 'end',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  font: {
                    size: 12
                  },
                  padding: 6
                }
              }
            }
          }
        }
      }
    });
    
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [novel]);
  
  return (
    <div className='sidebar-sidebar analytics-sidebar sidebar-analytics-cost'>
      <div style={{textAlign: 'right', cursor: 'pointer'}} onClick={onClose}>
        <Icon path={mdiClose} color={'#FFFFFF50'} size={1} />
      </div>
      
      <Flex direction="column" gap="3">
        <Text size="4" weight="bold" style={{color: 'white'}}>
          Cost Analysis
        </Text>
        
        <Box p="3" style={{backgroundColor: '#3c4748', borderRadius: '8px'}}>
          <Text size="2" style={{color: 'white', opacity: 0.9}}>
            Total cost for {novel?.title}: ${novel?.cost?.toFixed(6) || '0.000000'}
          </Text>
          
          <Box mt="4" style={{height: '400px', position: 'relative'}}>
            <canvas ref={chartRef}></canvas>
          </Box>
          
          <Text size="2" style={{color: 'white', opacity: 0.8, marginTop: '10px', fontStyle: 'italic', textAlign: 'center'}}>
            The chart shows cost distribution across scenes with AI-generated content
          </Text>
        </Box>
        
        <Box p="3" style={{backgroundColor: '#2c3839', borderRadius: '8px', marginTop: '10px'}}>
          <Text size="3" weight="bold" style={{color: 'white', marginBottom: '10px'}}>
            Cost Analysis Insights
          </Text>
          <ul style={{color: 'white', opacity: 0.8, paddingLeft: '20px'}}>
            <li>Longer, more complex scenes typically cost more to generate</li>
            <li>Costs are based on token usage for each API request</li>
            <li>More detailed instructions or context may increase costs</li>
            <li>Cost per scene may vary based on the complexity of the content</li>
          </ul>
        </Box>
      </Flex>
      
      <style jsx>{`
        .sidebar-analytics-cost {
          width: 900px !important;
        }
      `}</style>
    </div>
  );
};

export default SidebarAnalyticsCost;
